import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { MessageCircle, Mail, Loader2, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";

// Real applications.stage values behind friendlier audience-filter labels —
// see routes_campaigns.py's VALID_STAGES comment for why "Contacted" /
// "Documents Pending" map onto application_initiated/doc_verified rather
// than being literal stage names of their own.
const STAGE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "lead", label: "Lead Captured" },
  { value: "application_initiated", label: "Contacted" },
  { value: "doc_verified", label: "Documents Pending" },
  { value: "offer_issued", label: "Offer Issued" },
  { value: "fee_paid", label: "Fee Pending" },
];

const TEMPLATES = [
  { id: "offer_fee_reminder", label: "Offer Acceptance & Fee Reminder",
    message: "Hi {{first_name}}, this is a reminder from {{institution_name}} to confirm your seat for "
             + "{{course_name}} (App No: {{application_no}}) by accepting your offer and completing the "
             + "reservation fee payment." },
  { id: "document_verification_alert", label: "Document Verification Alert",
    message: "Hi {{first_name}}, our admissions team is reviewing your submitted documents for "
             + "{{course_name}} (App No: {{application_no}}). We'll notify you as soon as verification is "
             + "complete." },
  { id: "open_house_invite", label: "Campus Open House & Counseling Slot",
    message: "Hi {{first_name}}, you're invited to {{institution_name}}'s upcoming Campus Open House for "
             + "{{course_name}} applicants. Reply to this message to book your counseling slot." },
  { id: "general_inquiry_nurture", label: "General Inquiry Nurture",
    message: "Hi {{first_name}}, thanks for your interest in {{course_name}} at {{institution_name}}. "
             + "Let us know if you have any questions — we're here to help with your application." },
];

// Display-only estimate — the backend's own COST_PER_MESSAGE table in
// routes_campaigns.py is the source of truth for what actually gets logged.
const MARKETING_COST = { whatsapp: 0.86, email: 0.02 };

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const nf = (n) => Number(n || 0).toLocaleString("en-IN");

function highlightTags(text) {
  const parts = (text || "").split(/(\{\{\s*\w+\s*\}\})/g);
  return parts.map((p, i) =>
    /^\{\{\s*\w+\s*\}\}$/.test(p)
      ? <span key={i} className="bg-amber-100 text-amber-800 rounded px-1 font-medium">{p}</span>
      : <span key={i}>{p}</span>
  );
}

export default function BroadcastComposerModal({ open, onClose, onSuccess, channels, courses }) {
  const [campaignName, setCampaignName] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [stage, setStage] = useState("all");
  const [courseId, setCourseId] = useState("all");
  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset to a clean form every time the composer is (re)opened.
  useEffect(() => {
    if (!open) return;
    setCampaignName(""); setChannel("whatsapp"); setStage("all"); setCourseId("all");
    setTemplateId(TEMPLATES[0].id); setCount(null);
  }, [open]);

  // Debounced audience-count lookup whenever a filter changes.
  useEffect(() => {
    if (!open) return;
    setCountLoading(true);
    const t = setTimeout(() => {
      api.post("/campaigns/audience-count", {
        stage: stage === "all" ? null : stage,
        course_id: courseId === "all" ? null : courseId,
        source: null,
      })
        .then((r) => setCount(r.data.recipient_count))
        .catch(() => setCount(null))
        .finally(() => setCountLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [open, stage, courseId]);

  const template = TEMPLATES.find((t) => t.id === templateId) || TEMPLATES[0];
  const block = channels?.[channel];
  const remaining = block ? Math.max(0, block.quota - block.used) : null;
  const estimatedCost = count != null ? count * (MARKETING_COST[channel] || 0) : null;
  const overQuota = count != null && remaining != null && count > remaining;

  const canSubmit = campaignName.trim() && count != null && count > 0 && !overQuota && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/campaigns/broadcast", {
        campaign_name: campaignName.trim(),
        channel,
        target_filters: {
          stage: stage === "all" ? null : stage,
          course_id: courseId === "all" ? null : courseId,
          source: null,
        },
        template_id: templateId,
        message_preview: template.message,
      });
      toast.success(`Broadcast dispatched to ${nf(data.recipient_count)} candidates successfully.`);
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not launch this broadcast.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="broadcast-composer-modal">
        <DialogHeader>
          <DialogTitle className="font-display">New Broadcast</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Campaign Name</Label>
            <Input className="mt-1" placeholder="Round 2 Seat Reservation Reminder" value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)} data-testid="broadcast-campaign-name" />
          </div>

          <div>
            <Label className="text-xs">Channel</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setChannel("whatsapp")} data-testid="broadcast-channel-whatsapp"
                className={`h-10 rounded-md border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  channel === "whatsapp" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}>
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
              <button type="button" onClick={() => setChannel("email")} data-testid="broadcast-channel-email"
                className={`h-10 rounded-md border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  channel === "email" ? "bg-sky-600 border-sky-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}>
                <Mail className="w-4 h-4" /> Email
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="mt-1 h-9 text-sm bg-white" data-testid="broadcast-filter-stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Course</Label>
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger className="mt-1 h-9 text-sm bg-white" data-testid="broadcast-filter-course">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {(courses || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="broadcast-recipient-chip">
            {countLoading ? (
              <Badge className="bg-slate-100 text-slate-500"><Loader2 className="w-3 h-3 mr-1.5 animate-spin inline" /> Counting…</Badge>
            ) : (
              <Badge className="bg-emerald-100 text-emerald-800">
                <Users className="w-3 h-3 mr-1.5 inline" /> Target Audience: {count != null ? nf(count) : "—"} candidates
              </Badge>
            )}
          </div>

          <div>
            <Label className="text-xs">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="mt-1 h-9 text-sm bg-white" data-testid="broadcast-template-picker">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Message Preview</Label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 leading-relaxed"
              data-testid="broadcast-message-preview">
              {highlightTags(template.message)}
            </div>
          </div>

          <div className={`rounded-lg border px-3 py-2.5 text-xs space-y-1 ${
            overQuota ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-slate-50 text-slate-600"
          }`} data-testid="broadcast-cost-quota">
            <div className="flex items-center justify-between">
              <span>Estimated cost</span>
              <span className="font-medium">{estimatedCost != null ? money(estimatedCost) : "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Remaining {channel} quota this month</span>
              <span className="font-medium">{remaining != null ? `${nf(remaining)} / ${nf(block.quota)}` : "—"}</span>
            </div>
            {overQuota && (
              <div className="flex items-start gap-1.5 pt-1">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>This audience is larger than your remaining quota — narrow the filters or wait for next month.</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="broadcast-launch"
            className={channel === "whatsapp" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-sky-600 hover:bg-sky-700"}>
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Launching…</>) : "Launch Broadcast"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
